import React, { useCallback, useState } from 'react';

import { useTranslate } from '@src/core/hooks';
import { AUTH_MODE } from '@src/domains/customers';
import {
  EmailIdentificationCard,
  ForgotPassword,
  IDENTIFICATION_ORIGIN,
  Login,
  LOGIN_MODE,
  Signup,
  type EmailIdentificationPayload,
  type LoginMode,
} from '@src/features/customers';

import { useCheckoutTracking } from './analytics';
import { useCheckoutNavigation } from './CheckoutFlowContext';
import { AUTH_SUB_STEP, type AuthSubStep } from './authSubStep';

export { AUTH_SUB_STEP, type AuthSubStep };

export const CheckoutAuthStep = () => {
  // No step change on login here: `useCheckoutModeDecision` picks what comes next (confirmation,
  // payment, waiting), and a second decision from this step overrode it under StrictMode.
  const { authSubStep, setAuthSubStep } = useCheckoutNavigation();
  const { i18n } = useTranslate();
  const [email, setEmail] = useState('');
  // Only an email the account check found earns the "this email has an account" copy:
  // the login button skips the check, and the buyer can still edit the email on the login card.
  const [checkedAccountEmail, setCheckedAccountEmail] = useState('');

  const tracking = useCheckoutTracking();

  const handleSignup = useCallback(
    (nextEmail: string) => {
      setEmail(nextEmail);
      setAuthSubStep(AUTH_SUB_STEP.SIGNUP);
    },
    [setAuthSubStep],
  );

  const handleIdentify = useCallback(
    ({ email: nextEmail, mode, origin }: EmailIdentificationPayload) => {
      setEmail(nextEmail);
      setCheckedAccountEmail(
        origin === IDENTIFICATION_ORIGIN.accountCheck && mode === AUTH_MODE.LOGIN ? nextEmail : '',
      );
      setAuthSubStep(mode === AUTH_MODE.LOGIN ? AUTH_SUB_STEP.LOGIN : AUTH_SUB_STEP.SIGNUP);

      if (origin !== IDENTIFICATION_ORIGIN.accountCheck) {
        return;
      }

      if (mode === AUTH_MODE.LOGIN) {
        tracking.accountFound();
      } else {
        tracking.accountNotFound();
      }
    },
    [setAuthSubStep, tracking],
  );

  // The login screen opens on the password, so login_password_selected marks an explicit choice:
  // it only leaves when the buyer switches back to it from the code.
  const handleLoginModeSelected = useCallback(
    (loginMode: LoginMode) => {
      if (loginMode === LOGIN_MODE.OTP) {
        tracking.loginCodeSelected();
      } else {
        tracking.loginPasswordSelected();
      }
    },
    [tracking],
  );

  // TODO: Use routing when available after removing render from switch on CheckoutPageOutlet
  // (card: PR-4562)
  switch (authSubStep) {
    case AUTH_SUB_STEP.LOGIN:
      return (
        <Login
          initialEmail={email}
          onSignup={handleSignup}
          onForgotPassword={() => setAuthSubStep(AUTH_SUB_STEP.FORGOT_PASSWORD)}
          onChangeEmail={setEmail}
          onModeSelected={handleLoginModeSelected}
          onCodeSent={tracking.codeSent}
          onCodeValidated={tracking.codeValidated}
          onLoginCompleted={tracking.loginCompleted}
          submitLabel={i18n('checkout_auth_continue_purchase_cta')}
          passwordSubtitle={i18n(
            email && email === checkedAccountEmail
              ? 'checkout_auth_login_existing_account_subtitle'
              : 'checkout_auth_login_password_subtitle',
          )}
          signupLabel={i18n('checkout_auth_signup_link')}
        />
      );
    case AUTH_SUB_STEP.SIGNUP:
      return (
        <Signup
          email={email}
          onLogin={() => setAuthSubStep(AUTH_SUB_STEP.LOGIN)}
          onAccountCreated={tracking.accountCreated}
          onForgotPassword={(recoveryEmail) => {
            setEmail(recoveryEmail);
            setAuthSubStep(AUTH_SUB_STEP.FORGOT_PASSWORD);
          }}
        />
      );
    case AUTH_SUB_STEP.FORGOT_PASSWORD:
      return <ForgotPassword initialEmail={email} onBack={() => setAuthSubStep(AUTH_SUB_STEP.LOGIN)} />;
    case AUTH_SUB_STEP.IDENTIFICATION:
    default:
      return <EmailIdentificationCard onIdentify={handleIdentify} onEmailSubmit={tracking.emailSubmitted} />;
  }
};

export default CheckoutAuthStep;
